import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthProvider } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import NotFound from "./components/NotFound/NotFound";
import Home from "./pages/Home/Home";
import About from "./pages/About/About";
import Services from "./pages/Services/Services";
import ServiceDetail from "./pages/ServiceDetail/ServiceDetail";
import ProductDetail from "./pages/ProductDetail/ProductDetail";
import Consultation from "./pages/Consultation/Consultation";
import Hiring from "./pages/Hiring/Hiring";
import Contact from "./pages/Contact/Contact";
import Login from "./pages/Auth/Login";
import Register from "./pages/Auth/Register";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import ResetPassword from "./pages/Auth/ResetPassword";
import OAuthCallback from "./pages/Auth/OAuthCallback";
import Profile from "./pages/Profile/Profile";
import AdminLayout from "./layouts/AdminLayout/AdminLayout";
import Overview from "./pages/Dashboard/Overview/Overview";
import UsersPage from "./pages/Dashboard/UsersPage/UsersPage";
import MessagesPage from "./pages/Dashboard/MessagesPage/MessagesPage";
import BookingsPage from "./pages/Dashboard/BookingsPage/BookingsPage";
import ServicesPage from "./pages/Dashboard/ServicesPage/ServicesPage";
import ProductsPage from "./pages/Dashboard/ProductsPage/ProductsPage";
import QuotationsPage from "./pages/Dashboard/QuotationsPage/QuotationsPage";
import ConsultationServicesPage from "./pages/Dashboard/ConsultationServicesPage/ConsultationServicesPage";
import TeamPage from "./pages/Dashboard/TeamPage/TeamPage";
import MyBookings from "./pages/MyBookings/MyBookings";
import TawkChat from "./components/tawkChat/TawkChat";
import { pageViewService } from "./services/pageViewService";

function PublicLayout() {
  const location = useLocation();

  useEffect(() => {
    const publicPages = ["/", "/about", "/services", "/consultation", "/hiring", "/contact"];
    if (publicPages.includes(location.pathname)) {
      pageViewService.trackPageView(location.pathname).catch((err) => {
        console.error("Failed to track page view:", err);
      });
    }
  }, [location.pathname]);

  return (
    <>
      <Navbar />
      <TawkChat />
      {/* <CrispChat /> */}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:id" element={<ServiceDetail />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/consultation" element={<Consultation />} />
        <Route path="/hiring" element={<Hiring />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-bookings"
          element={
            <ProtectedRoute>
              <MyBookings />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
    </>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <Routes>
        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={["ADMIN"]}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Overview />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="bookings" element={<BookingsPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="quotations" element={<QuotationsPage />} />
          <Route path="consultation-services" element={<ConsultationServicesPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    );
  }

  return <PublicLayout />;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <ToastContainer
          position="top-right"
          autoClose={4500}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          theme="light"
        />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
